import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of, ReplaySubject, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SubscriptionPaymentDialogComponent } from '../../../dialogs/user-subscripion-payment-dialog/user-subscription-payment-dialog.component';
import { UserService } from '../../../service/user.abstract.service';
import { FESubscription, UserOrgSubscriptionStatus, UserSubscription } from '../../../user.model';
import { ActivatedRoute } from '@angular/router';
import { SubscriptionManagementDialogComponent } from '../../../dialogs/user-subscription-management-dialog/user-subscription-management-dialog.component';
import { BcitiConfirmDialogComponent } from 'src/app/global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import { SubscriptionComponent } from '../subscription/subscription.component';
import { CardConfiguration } from '@bciti/types';
import { organisationConfigurationSelector, languageSelector } from '../../../../global/store/global.selectors';
import { userRecordSelector } from 'src/app/user/store/user.selectors';
import { MessageService } from '../../../../global/services/message/message.abstract.service';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { Actions, ofType } from '@ngrx/effects';
import { RefreshUserSubscriptionsAction } from 'src/app/user/store/user.actions';

@Component({
    selector: 'app-user-subscriptions',
    templateUrl: './user-subscriptions.component.html',
    styleUrls: ['./user-subscriptions.component.scss'],
})
export class UserSubscriptionsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();
    private update$ = new ReplaySubject();

    UserOrgSubscriptionStatus = UserOrgSubscriptionStatus;

    loading = false;

    userId: string;

    activeSubscriptions: FESubscription[] = [];
    inactiveSubscriptions: FESubscription[] = [];
    availableSubscriptions: FESubscription[] = [];

    public cardConfig: CardConfiguration[] = [];

    subscribeForm: FormGroup = new FormGroup({});

    constructor(
        private router: ActivatedRoute,
        private translateService: TranslateService,
        private userService: UserService,
        private dialog: MatDialog,
        private messageService: MessageService,
        private store: Store<State>,
        private actions$: Actions,
    ) {}

    get enablePaymentDialog() {
        let enable = false;
        Object.keys(this.subscribeForm.controls).forEach((key) => {
            if (this.subscribeForm.controls[key].value) {
                enable = true;
            }
        });
        return enable;
    }

    ngOnInit(): void {
        this.router.paramMap.subscribe((paramMap) => (this.userId = paramMap.get('id')));
        this.store
            .select(languageSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((lang) => this.translateService.use(lang)),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(RefreshUserSubscriptionsAction),
                tap(() => this.update$.next()),
            )
            .subscribe();

        this.update$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => (this.loading = true)),
                switchMap(() =>
                    this.store.select(userRecordSelector).pipe(
                        takeUntil(this.destroy$),
                        filter((userRecord) => !!userRecord),
                    ),
                ),
                switchMap((userRecord) =>
                    combineLatest([this.store.select(organisationConfigurationSelector), this.userService.getUserSubscriptions(userRecord.id)]).pipe(
                        takeUntil(this.destroy$),
                        map(([config, userSubs]) => {
                            this.activeSubscriptions = [];
                            this.inactiveSubscriptions = [];
                            this.availableSubscriptions = [];

                            this.subscribeForm.reset();

                            userSubs.forEach((uSub) => {
                                const cardConfig = config.support.cards.find((c) => c.type === uSub.cardType);
                                const sub: FESubscription = uSub;
                                sub.cardLabel = cardConfig.identity?.label[this.translateService.currentLang];
                                sub.cardIcon = cardConfig.identity?.icon;
                                switch (uSub.status) {
                                    case UserOrgSubscriptionStatus.ACTIVE:
                                        this.activeSubscriptions.push(sub as FESubscription);
                                        break;
                                    case UserOrgSubscriptionStatus.INACTIVE:
                                        this.inactiveSubscriptions.push(sub as FESubscription);
                                        break;
                                    default:
                                        this.availableSubscriptions.push(sub as FESubscription);
                                        this.subscribeForm.addControl(uSub.code, new FormControl());
                                }
                            });

                            this.activeSubscriptions.sort((a, b) => a.order - b.order);
                            this.inactiveSubscriptions.sort((a, b) => a.order - b.order);
                            this.availableSubscriptions.sort((a, b) => a.order - b.order);
                            this.loading = false;
                        }),
                    ),
                ),
            )
            .subscribe();

        this.translateService.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap(() => this.update$.next()),
            )
            .subscribe();

        this.update$.next();
    }

    public openPaymentDialog() {
        const codesToAdd: FESubscription[] = [];
        Object.keys(this.subscribeForm.controls).forEach((code) => {
            if (this.subscribeForm.controls[code].value) {
                codesToAdd.push(this.availableSubscriptions.find((sub) => sub.code === code));
            }
        });

        if (codesToAdd.length === 0) return;

        this.dialog
            .open(SubscriptionPaymentDialogComponent, {
                data: codesToAdd,
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) this.update$.next();
            });
    }

    public openRenewalDialog(sub: FESubscription): void {
        this.dialog
            .open(SubscriptionPaymentDialogComponent, {
                data: [{ ...sub, purposeStartDate: undefined, purposeEndDate: undefined }],
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) this.update$.next();
            });
    }

    public handleDetailsDialogPrompt(sub: FESubscription) {
        this.dialog
            .open(SubscriptionManagementDialogComponent, { data: sub, panelClass: 'subscription-management-dialog' })
            .afterClosed()
            .subscribe((result) => {
                if (result) this.update$.next();
            });
    }

    public handleDeleteDialogPrompt(sub: FESubscription) {
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            maxWidth: '750px',
            data: {
                dialog: {
                    title: this.translateService.instant('pages.user-subscriptions.delete-dialog.title'),
                    body: this.translateService.instant('pages.user-subscriptions.delete-dialog.body'),
                    icon: 'icon-warning',
                    iconColor: 'red',
                    confirmLabel: this.translateService.instant('pages.user-subscriptions.delete-dialog.confirm'),
                    cancelLabel: this.translateService.instant('pages.user-subscriptions.delete-dialog.cancel'),
                    confirmButtonClass: 'bciti-button--red',
                },
                component: {
                    entry: SubscriptionComponent,
                    data: { item: sub },
                },
            },
        });

        dialogRef.componentInstance.onSubmit.subscribe((e) => {
            dialogRef.componentInstance.dialogRef.close();
            if (e) {
                this.deleteUserSubscription(this.userId, sub);
            }
        });
    }

    private deleteUserSubscription(userId: string, sub: FESubscription) {
        this.loading = true;
        this.userService
            .deleteSubscription(userId, sub._id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.messageService.success(this.translateService.instant('pages.user-subscriptions.delete-dialog.messages.success'));
                    this.update$.next();
                },
                (error) => {
                    this.messageService.error(this.translateService.instant('pages.user-subscriptions.delete-dialog.messages.error'));
                    this.loading = false;
                },
            );
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
